import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import {
  Building2, Mail, Phone, MapPin, Wrench, PoundSterling,
  Calendar, Pencil, MessageSquarePlus, Send, Sparkles, Loader2, Globe, User
} from "lucide-react";
import { cn } from "@/lib/utils";

const stageConfig = {
  new: { label: "New", cls: "bg-blue-500/10 text-blue-400 border-blue-500/20" },
  contacted: { label: "Contacted", cls: "bg-indigo-500/10 text-indigo-400 border-indigo-500/20" },
  qualified: { label: "Qualified", cls: "bg-purple-500/10 text-purple-400 border-purple-500/20" },
  proposal: { label: "Proposal", cls: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20" },
  negotiation: { label: "Negotiation", cls: "bg-orange-500/10 text-orange-400 border-orange-500/20" },
  won: { label: "Won", cls: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" },
  lost: { label: "Lost", cls: "bg-red-500/10 text-red-400 border-red-500/20" },
};

const sourceLabels = {
  website: "Website", phone: "Phone", referral: "Referral",
  "walk-in": "Walk-in", email: "Email", trade_show: "Trade Show",
  social_media: "Social Media", google_places: "Google Places",
};

export const LeadDetail = ({ lead, open, onOpenChange, onEdit, onRefresh }) => {
  const [localLead, setLocalLead] = useState(lead);
  const [noteText, setNoteText] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  const [enriching, setEnriching] = useState(false);

  useEffect(() => { setLocalLead(lead); }, [lead]);

  if (!localLead) return null;

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    setAddingNote(true);
    try {
      await api.post(`/leads/${localLead.id}/notes`, { content: noteText.trim() });
      toast.success("Note added");
      setNoteText("");
      const res = await api.get(`/leads/${localLead.id}`);
      setLocalLead(res.data);
      onRefresh?.();
    } catch {
      toast.error("Failed to add note");
    } finally {
      setAddingNote(false);
    }
  };

  const handleEnrich = async () => {
    if (!localLead.website && !localLead.description?.includes('http')) {
      toast.error("No website URL available for this lead. Edit the lead to add one.");
      return;
    }
    setEnriching(true);
    try {
      const res = await api.post(`/leads/${localLead.id}/enrich`);
      setLocalLead(res.data);
      onRefresh?.();
      if (res.data.enrichment_status === 'done') {
        toast.success("Lead enriched — contact info found");
      } else {
        toast.warning(res.data.enrichment_notes || "Enrichment completed with no results");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Enrichment failed");
    } finally {
      setEnriching(false);
    }
  };

  const stage = stageConfig[localLead.stage] || { label: localLead.stage, cls: "" };
  const hasEnrichmentResults = localLead.enrichment_status === 'done' && (
    localLead.enrichment_emails?.length > 0 ||
    localLead.enrichment_contacts?.length > 0 ||
    localLead.enrichment_description
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[580px] bg-card border-white/10 backdrop-blur-xl max-h-[90vh] overflow-y-auto" data-testid="lead-detail-dialog">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-sm bg-blue-500/10 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-blue-400" strokeWidth={1.5} />
              </div>
              <div>
                <DialogTitle className="text-xl tracking-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  {localLead.company_name}
                </DialogTitle>
                <DialogDescription className="text-sm text-muted-foreground">
                  {localLead.contact_name}
                </DialogDescription>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onEdit?.(localLead)}
              className="rounded-sm border-white/10 text-xs uppercase tracking-wider"
              data-testid="lead-detail-edit"
            >
              <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
            </Button>
          </div>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Status Row */}
          <div className="flex items-center flex-wrap gap-2">
            <Badge variant="outline" className={cn("text-xs rounded-sm", stage.cls)}>
              {stage.label}
            </Badge>
            <div className="flex items-center gap-1">
              <PoundSterling className="w-3.5 h-3.5 text-amber-400" />
              <span className="text-sm font-bold text-amber-400">
                {Number(localLead.estimated_value).toLocaleString()}
              </span>
            </div>
            <Badge variant="outline" className="text-[10px] rounded-sm">{localLead.priority} priority</Badge>
          </div>

          <Separator className="bg-white/5" />

          {/* Details Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {localLead.email && (
              <a href={`mailto:${localLead.email}`} className="flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors duration-150" data-testid="lead-detail-email-link">
                <Mail className="w-4 h-4" strokeWidth={1.5} />
                <span className="text-sm truncate">{localLead.email}</span>
              </a>
            )}
            {localLead.phone && (
              <a href={`tel:${localLead.phone}`} className="flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors duration-150" data-testid="lead-detail-phone-link">
                <Phone className="w-4 h-4" strokeWidth={1.5} />
                <span className="text-sm">{localLead.phone}</span>
              </a>
            )}
            <div className="flex items-center gap-2">
              <Wrench className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
              <span className="text-sm text-foreground">{localLead.service_type}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
              <span className="text-sm text-muted-foreground">
                {sourceLabels[localLead.lead_source] || localLead.lead_source}
              </span>
            </div>
            {localLead.website && (
              <div className="flex items-center gap-2 col-span-2">
                <Globe className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                <a href={localLead.website} target="_blank" rel="noopener noreferrer"
                   className="text-sm text-blue-400 hover:text-blue-300 truncate">{localLead.website}</a>
              </div>
            )}
            {localLead.address && (
              <div className="flex items-center gap-2 col-span-2">
                <MapPin className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                <span className="text-sm text-foreground">{localLead.address}</span>
              </div>
            )}
          </div>

          {localLead.description && (
            <>
              <Separator className="bg-white/5" />
              <div>
                <p className="text-xs font-bold tracking-widest uppercase text-muted-foreground mb-1.5">Description</p>
                <p className="text-sm text-foreground leading-relaxed">{localLead.description}</p>
              </div>
            </>
          )}

          <Separator className="bg-white/5" />

          {/* AI Enrichment */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                <p className="text-xs font-bold tracking-widest uppercase text-muted-foreground">AI Enrichment</p>
                {localLead.enrichment_status === 'done' && (
                  <Badge variant="outline" className="text-[10px] rounded-sm bg-emerald-500/10 text-emerald-400 border-emerald-500/20">Done</Badge>
                )}
                {localLead.enrichment_status === 'failed' && (
                  <Badge variant="outline" className="text-[10px] rounded-sm bg-red-500/10 text-red-400 border-red-500/20">Failed</Badge>
                )}
                {localLead.enrichment_status === 'pending' && (
                  <Badge variant="outline" className="text-[10px] rounded-sm bg-yellow-500/10 text-yellow-400 border-yellow-500/20">Pending</Badge>
                )}
              </div>
              <Button
                size="sm"
                onClick={handleEnrich}
                disabled={enriching}
                className="rounded-sm bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 border border-blue-500/20 text-xs h-8 px-3"
                data-testid="lead-detail-enrich-btn"
              >
                {enriching
                  ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Enriching...</>
                  : <><Sparkles className="w-3.5 h-3.5 mr-1.5" />Enrich with AI</>
                }
              </Button>
            </div>

            {hasEnrichmentResults && (
              <div className="space-y-3 rounded-sm bg-white/[0.02] border border-white/5 p-3">
                {localLead.enrichment_description && (
                  <p className="text-sm text-muted-foreground leading-relaxed">{localLead.enrichment_description}</p>
                )}

                {localLead.enrichment_emails?.length > 0 && (
                  <div>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-widest mb-1.5">Found Emails</p>
                    <div className="flex flex-wrap gap-2">
                      {localLead.enrichment_emails.map((email, i) => (
                        <a key={i} href={`mailto:${email}`}
                           className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1">
                          <Mail className="w-3 h-3" />{email}
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {localLead.enrichment_contacts?.length > 0 && (
                  <div>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-widest mb-1.5">Key Contacts</p>
                    <div className="space-y-2">
                      {localLead.enrichment_contacts.map((c, i) => (
                        <div key={i} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                          <div className="flex items-center gap-1.5">
                            <User className="w-3 h-3 text-muted-foreground" />
                            <span className="font-medium text-foreground">{c.name}</span>
                          </div>
                          {c.role && <span className="text-muted-foreground">{c.role}</span>}
                          {c.email && (
                            <a href={`mailto:${c.email}`} className="text-blue-400 hover:text-blue-300 flex items-center gap-1">
                              <Mail className="w-3 h-3" />{c.email}
                            </a>
                          )}
                          {c.phone && (
                            <a href={`tel:${c.phone}`} className="text-blue-400 hover:text-blue-300 flex items-center gap-1">
                              <Phone className="w-3 h-3" />{c.phone}
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {localLead.enrichment_status === 'done' && !hasEnrichmentResults && (
              <p className="text-xs text-muted-foreground/50">No contact information found on the website.</p>
            )}
            {localLead.enrichment_status === 'failed' && localLead.enrichment_notes && (
              <p className="text-xs text-red-400/70">{localLead.enrichment_notes}</p>
            )}
            {(!localLead.enrichment_status || localLead.enrichment_status === 'none') && !enriching && (
              <p className="text-xs text-muted-foreground/50">
                Scrapes the company website using AI to find email addresses, key contacts, and a description.
              </p>
            )}
          </div>

          <Separator className="bg-white/5" />

          {/* Notes */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <MessageSquarePlus className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
              <p className="text-xs font-bold tracking-widest uppercase text-muted-foreground">
                Notes ({localLead.notes?.length || 0})
              </p>
            </div>

            <div className="flex items-center gap-2 mb-3">
              <Input
                data-testid="lead-detail-note-input"
                value={noteText}
                onChange={e => setNoteText(e.target.value)}
                placeholder="Add a note..."
                className="h-9 bg-secondary/50 border-white/5 rounded-sm text-sm flex-1"
                onKeyDown={e => e.key === "Enter" && handleAddNote()}
              />
              <Button
                size="sm"
                onClick={handleAddNote}
                disabled={addingNote || !noteText.trim()}
                className="rounded-sm bg-blue-500 hover:bg-blue-600 h-9 px-3"
                data-testid="lead-detail-add-note"
              >
                <Send className="w-3.5 h-3.5" />
              </Button>
            </div>

            <div className="space-y-2 max-h-[180px] overflow-y-auto">
              {localLead.notes && localLead.notes.length > 0 ? (
                [...localLead.notes].reverse().map((note) => (
                  <div
                    key={note.id}
                    data-testid={`lead-note-${note.id}`}
                    className="p-2.5 rounded-sm bg-muted/50 border border-white/5"
                  >
                    <p className="text-sm text-foreground">{note.content}</p>
                    <p className="text-[10px] text-muted-foreground mt-1">
                      {new Date(note.created_at).toLocaleString()}
                    </p>
                  </div>
                ))
              ) : (
                <p className="text-xs text-muted-foreground/50 text-center py-4">No notes yet</p>
              )}
            </div>
          </div>

          {/* Timestamps */}
          <div className="flex items-center justify-between text-[10px] text-muted-foreground pt-2">
            <span>Created: {new Date(localLead.created_at).toLocaleDateString()}</span>
            <span>Updated: {new Date(localLead.updated_at).toLocaleDateString()}</span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
